import type { CrmOpportunity, EnrichedPart, ErpPurchaseOrder } from "../domain/types";
import type { DemoGeneratorConfig } from "./generatorConfig";
import { SeededRandom } from "./seededRandom";

const ACCOUNT_NAMES = [
    "Axiom Defense",
    "Meridian Medical",
    "Cascadia Robotics",
    "Vantara Systems",
    "Northstar Controls",
    "Summit Industrial",
];

const OWNERS = ["Pat Kim", "Morgan Diaz", "Riley Shah", "Alex Grant", "Jordan Lee"];
const BUYERS = ["Taylor Reed", "Jamie Ellis", "Casey Morgan", "Robin Chen"];
const SUPPLIERS = ["Arrow", "Avnet", "Digi-Key", "Mouser", "TTI", "Future Electronics"];

const findCommerciallyInterestingParts = (parts: EnrichedPart[]): EnrichedPart[] => {
    const candidates = parts.filter(
        (part) =>
            part.lifecycleStatus === "EOL" ||
            part.lifecycleStatus === "NRND" ||
            part.hasComplianceChange ||
            part.confidenceScore < 80,
    );

    return candidates.length > 0 ? candidates : parts;
};

export const generateCommercialActivity = (
    config: Pick<DemoGeneratorConfig, "opportunityCount" | "purchaseOrderCount">,
    rng: SeededRandom,
    parts: EnrichedPart[],
): { opportunities: CrmOpportunity[]; purchaseOrders: ErpPurchaseOrder[] } => {
    const interestingParts = findCommerciallyInterestingParts(parts);
    const opportunities: CrmOpportunity[] = [];
    const purchaseOrders: ErpPurchaseOrder[] = [];

    for (let index = 0; index < config.opportunityCount; index += 1) {
        const part = interestingParts[(index * 5) % interestingParts.length]!;
        const accountSegment = rng.pick(["Commercial", "Medical", "Defense", "Industrial"] as const);

        opportunities.push({
            opportunityId: `OPP-${String(index + 1).padStart(5, "0")}`,
            accountName: rng.pick(ACCOUNT_NAMES),
            accountSegment,
            partId: part.partId,
            mpn: part.mpn,
            quantity: rng.pick([100, 250, 500, 1000, 5000, 10000]),
            quotedMarginPct: rng.float(8, 34, 2),
            owner: rng.pick(OWNERS),
            regulatedAccount: accountSegment === "Medical" || accountSegment === "Defense",
        });
    }

    for (let index = 0; index < config.purchaseOrderCount; index += 1) {
        const part = interestingParts[(index * 11) % interestingParts.length]!;

        purchaseOrders.push({
            purchaseOrderId: `PO-${String(index + 1).padStart(5, "0")}`,
            supplierName: rng.pick(SUPPLIERS),
            partId: part.partId,
            mpn: part.mpn,
            quantity: rng.pick([50, 100, 250, 500, 1000, 2500]),
            dueDate: `2026-06-${String((index % 28) + 1).padStart(2, "0")}`,
            buyer: rng.pick(BUYERS),
            lineStatus: rng.pick(["Open", "Expedite", "Hold"] as const),
        });
    }

    return { opportunities, purchaseOrders };
};

